import React from 'react';
import { Shield } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import SEO from '../components/SEO';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen">
      <SEO
        title="Privacy Policy | NIVA Digital AI"
        description="Read NIVA Digital's privacy policy to understand how we collect, use, and protect your personal information."
        keywords="privacy policy, data protection, NIVA Digital privacy"
        canonical={`${process.env.REACT_APP_BASE_URL || 'https://nivadigital.io'}/privacy-policy`}
      />
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center">
            <div className="inline-flex items-center space-x-2 bg-red-100 text-red-600 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Shield size={20} />
              <span>Privacy & Terms</span>
            </div>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 mb-6 leading-tight">
              Privacy Policy & Terms of Service
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Your privacy and security are our top priorities
            </p>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-4xl">
          <Card className="border-2">
            <CardContent className="pt-8">
              <div className="prose prose-lg max-w-none">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Privacy Policy</h2>
                
                <h3 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Information We Collect</h3>
                <p className="text-gray-600 mb-4">
                  We collect information that you provide directly to us, including when you create an account, 
                  request services, or communicate with us. This may include your name, email address, phone number, 
                  company information, and other contact details.
                </p>

                <h3 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">How We Use Your Information</h3>
                <p className="text-gray-600 mb-4">
                  We use the information we collect to:
                </p>
                <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
                  <li>Provide, maintain, and improve our services</li>
                  <li>Process and complete transactions</li>
                  <li>Send you technical notices, updates, and support messages</li>
                  <li>Respond to your comments, questions, and requests</li>
                  <li>Communicate with you about products, services, and events</li>
                </ul>

                <h3 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Data Security</h3>
                <p className="text-gray-600 mb-4">
                  We implement appropriate technical and organizational measures to protect the security of your 
                  personal information. However, please note that no method of transmission over the Internet or 
                  electronic storage is 100% secure.
                </p>

                <h3 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Cookies</h3>
                <p className="text-gray-600 mb-4">
                  We use cookies and similar tracking technologies to collect and track information and to improve 
                  and analyze our services. You can instruct your browser to refuse all cookies or to indicate when 
                  a cookie is being sent.
                </p>

                <h2 className="text-3xl font-bold text-gray-900 mb-6 mt-12">Terms of Service</h2>

                <h3 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Acceptance of Terms</h3>
                <p className="text-gray-600 mb-4">
                  By accessing and using NIVA Digital's services, you accept and agree to be bound by the terms 
                  and provisions of this agreement.
                </p>

                <h3 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Use of Services</h3>
                <p className="text-gray-600 mb-4">
                  You agree to use our services only for lawful purposes and in accordance with these Terms. 
                  You agree not to use our services in any way that violates any applicable laws or regulations.
                </p>

                <h3 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Intellectual Property</h3>
                <p className="text-gray-600 mb-4">
                  The services and their entire contents, features, and functionality are owned by NIVA Digital, LLC 
                  and are protected by United States and international copyright, trademark, and other intellectual 
                  property laws.
                </p>

                <h3 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Limitation of Liability</h3>
                <p className="text-gray-600 mb-4">
                  In no event shall NIVA Digital, LLC, nor its directors, employees, partners, agents, suppliers, 
                  or affiliates, be liable for any indirect, incidental, special, consequential, or punitive damages.
                </p>

                <h3 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Contact Us</h3>
                <p className="text-gray-600 mb-4">
                  If you have any questions about these Terms or our Privacy Policy, please contact us at:
                </p>
                <p className="text-gray-600 mb-2">
                  <strong>Email:</strong> info@nivadigital.io
                </p>
                <p className="text-gray-600">
                  <strong>Phone:</strong> +1 (303) 219-0207
                </p>

                <p className="text-gray-500 text-sm mt-8">
                  Last updated: January 2025
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;